/**
 * ExpressrContext — Central state for the creative writing hub.
 *
 * Composes:
 * - contextr: Application/workspace context awareness
 * - expressr-lib: Core synthesis engine
 * - snappr-lib: Snapshot management (via SnapshotManager)
 * - outputtr-lib: Export orchestration (via OutputtrManager)
 * - listr-lib: Document list management (via ListrManager)
 *
 * The context wires these existing packages together;
 * it does NOT reimplement their functionality.
 */

import React, { createContext, useContext, useReducer, useCallback, useRef, useEffect } from 'react';
import { Expressr } from '@opcentral/expressr-lib';
import type { SynthesisOptions, SynthesisResult } from '@opcentral/expressr-lib';
import type { ExpressrDocument, ExpressrUIConfig, DocumentFormat, KeeprLike, FylrLike, MattrLike } from '../types.js';

// ── Browser-safe interfaces for Node-only packages ──
// snappr-lib and outputtr-lib require Node builtins (fs, child_process).
// In the browser we use no-op stubs; in Electron/Node the real packages
// can be injected via config or dynamic import.

interface SnapshotManagerLike {
  createSnapshot: (...args: any[]) => Promise<any>;
  listSnapshots: (...args: any[]) => Promise<any[]>;
  revertToSnapshot: (...args: any[]) => Promise<any>;
}

interface OutputOrchestratorLike {
  generate: (...args: any[]) => Promise<any>;
}

const browserSnapshots: SnapshotManagerLike = {
  createSnapshot: async () => ({ id: 'browser-stub', message: 'Snapshots require Node runtime' }),
  listSnapshots: async () => [],
  revertToSnapshot: async () => ({}),
};

const browserOutputtr: OutputOrchestratorLike = {
  generate: async () => ({ content: '' }),
};

// ── Browser-safe stubs for keepr, fylr, mattr ──

const browserKeepr: KeeprLike = {
  publish: async () => ({ success: true, knowledgeId: 'browser-stub' }),
  get: async () => null,
  search: async () => ({ knowledge: [], total: 0 }),
  getSources: async () => [],
  registerSource: async () => {},
};

const browserFylr: FylrLike = {
  export: (_data, filename) => ({ success: true, filename, size: 0 }),
  detectFormat: () => 'text',
  getRecentFiles: () => [],
};

const browserMattr: MattrLike = {
  createMatter: async (type) => ({ id: `mattr-${Date.now()}`, type, sequence: 0, status: 'pending' }),
  updateMatter: async (matterId) => ({ id: matterId }),
  addEvent: async (matterId) => ({ id: `evt-${Date.now()}`, matterId }),
  getMatter: async () => null,
  getMatterSequence: async (matterId) => ({ matterId, events: [], eventCount: 0 }),
};

// ── State ──

export type ViewMode = 'edit' | 'preview' | 'split';

interface ExpressrState {
  documents: ExpressrDocument[];
  activeDocumentId: string | null;
  generating: boolean;
  saving: boolean;
  lastSynthesis: SynthesisResult | null;
  sidePanel: 'none' | 'synthesis' | 'snapshots' | 'export' | 'documents' | 'reversr';
  filter: string;
  hasUnsavedChanges: boolean;
  viewMode: ViewMode;
  documentThemeId: string;
}

type Action =
  | { type: 'ADD_DOCUMENT'; document: ExpressrDocument }
  | { type: 'UPDATE_DOCUMENT'; id: string; updates: Partial<ExpressrDocument> }
  | { type: 'DELETE_DOCUMENT'; id: string }
  | { type: 'SELECT_DOCUMENT'; id: string | null }
  | { type: 'SET_GENERATING'; generating: boolean }
  | { type: 'SET_SAVING'; saving: boolean }
  | { type: 'SET_LAST_SYNTHESIS'; result: SynthesisResult | null }
  | { type: 'SET_SIDE_PANEL'; panel: ExpressrState['sidePanel'] }
  | { type: 'SET_FILTER'; filter: string }
  | { type: 'SET_UNSAVED'; hasUnsavedChanges: boolean }
  | { type: 'SET_VIEW_MODE'; viewMode: ViewMode }
  | { type: 'SET_DOCUMENT_THEME'; themeId: string };

function reducer(state: ExpressrState, action: Action): ExpressrState {
  switch (action.type) {
    case 'ADD_DOCUMENT':
      return { ...state, documents: [...state.documents, action.document] };
    case 'UPDATE_DOCUMENT':
      return {
        ...state,
        documents: state.documents.map((d) =>
          d.id === action.id ? { ...d, ...action.updates, updatedAt: new Date() } : d
        ),
      };
    case 'DELETE_DOCUMENT': {
      const filtered = state.documents.filter((d) => d.id !== action.id);
      return {
        ...state,
        documents: filtered,
        activeDocumentId: state.activeDocumentId === action.id ? null : state.activeDocumentId,
      };
    }
    case 'SELECT_DOCUMENT':
      return { ...state, activeDocumentId: action.id, hasUnsavedChanges: false };
    case 'SET_GENERATING':
      return { ...state, generating: action.generating };
    case 'SET_SAVING':
      return { ...state, saving: action.saving };
    case 'SET_LAST_SYNTHESIS':
      return { ...state, lastSynthesis: action.result };
    case 'SET_SIDE_PANEL':
      return { ...state, sidePanel: action.panel };
    case 'SET_FILTER':
      return { ...state, filter: action.filter };
    case 'SET_UNSAVED':
      return { ...state, hasUnsavedChanges: action.hasUnsavedChanges };
    case 'SET_VIEW_MODE':
      return { ...state, viewMode: action.viewMode };
    case 'SET_DOCUMENT_THEME':
      return { ...state, documentThemeId: action.themeId };
    default:
      return state;
  }
}

const initialState: ExpressrState = {
  documents: [],
  activeDocumentId: null,
  generating: false,
  saving: false,
  lastSynthesis: null,
  sidePanel: 'documents',
  filter: '',
  hasUnsavedChanges: false,
  viewMode: 'edit' as ViewMode,
  documentThemeId: 'editor-dark',
};

// ── Context type ──

interface ExpressrContextType {
  state: ExpressrState;
  config: ExpressrUIConfig;
  /** expressr-lib instance */
  expressr: Expressr;
  /** snappr-lib instance for version snapshots */
  snapshots: SnapshotManagerLike;
  /** outputtr-lib instance for export */
  outputtr: OutputOrchestratorLike;
  /** keepr-lib instance for permanent knowledge storage */
  keepr: KeeprLike;
  /** fylr-lib instance for file export/import */
  fylr: FylrLike;
  /** mattr-lib instance for document-as-mattr tracking */
  mattr: MattrLike;
  // Document operations
  createDocument: (title: string, format?: DocumentFormat) => ExpressrDocument;
  updateDocument: (id: string, updates: Partial<ExpressrDocument>) => void;
  deleteDocument: (id: string) => void;
  selectDocument: (id: string | null) => void;
  saveDocument: () => Promise<void>;
  updateContent: (content: string) => void;
  // Synthesis (delegates to expressr-lib)
  synthesize: (options: SynthesisOptions) => Promise<SynthesisResult>;
  insertSynthesis: (content: string) => void;
  // Navigation
  setSidePanel: (panel: ExpressrState['sidePanel']) => void;
  setFilter: (filter: string) => void;
  // Export (delegates to outputtr-lib)
  exportDocument: (format: string) => Promise<void>;
  // View mode & theme
  setViewMode: (mode: ViewMode) => void;
  setDocumentTheme: (themeId: string) => void;
  // Keepr (permanent knowledge storage)
  publishToKeepr: (category?: string, tags?: string[]) => Promise<{ success: boolean; knowledgeId?: string }>;
  // Fylr (file export)
  exportViaFylr: (filename?: string) => { success: boolean; filename: string };
  // Computed
  activeDocument: ExpressrDocument | null;
  filteredDocuments: ExpressrDocument[];
}

const ExpressrContext = createContext<ExpressrContextType | null>(null);

// ── Provider ──

export function ExpressrProvider({
  config,
  children,
}: {
  config: ExpressrUIConfig;
  children: React.ReactNode;
}) {
  const [state, dispatch] = useReducer(reducer, initialState);

  // Wire existing package instances
  const expressrRef = useRef(new Expressr({ llm: config.llm }));
  const snapshotsRef = useRef<SnapshotManagerLike>(browserSnapshots);
  const outputtrRef = useRef<OutputOrchestratorLike>(browserOutputtr);
  const keeprRef = useRef<KeeprLike>(config.keepr ?? browserKeepr);
  const fylrRef = useRef<FylrLike>(config.fylr ?? browserFylr);
  const mattrRef = useRef<MattrLike>(config.mattr ?? browserMattr);
  const idCounter = useRef(1);

  // Computed
  const activeDocument = state.activeDocumentId
    ? state.documents.find((d) => d.id === state.activeDocumentId) || null
    : null;

  const filteredDocuments = state.filter
    ? state.documents.filter(
        (d) =>
          d.title.toLowerCase().includes(state.filter.toLowerCase()) ||
          d.tags.some((t) => t.toLowerCase().includes(state.filter.toLowerCase()))
      )
    : state.documents;

  // ── Document operations ──

  const createDocument = useCallback(
    (title: string, format: DocumentFormat = 'markdown'): ExpressrDocument => {
      const doc: ExpressrDocument = {
        id: `doc-${Date.now()}-${idCounter.current++}`,
        title,
        content: '',
        format,
        status: 'draft',
        tags: [],
        createdAt: new Date(),
        updatedAt: new Date(),
        wordCount: 0,
      };
      dispatch({ type: 'ADD_DOCUMENT', document: doc });
      dispatch({ type: 'SELECT_DOCUMENT', id: doc.id });

      // Auto-register as a mattr (document-as-mattr pattern).
      // Fire-and-forget: mattr registration is non-blocking.
      mattrRef.current.createMatter('document', {
        documentId: doc.id,
        title,
        format,
      }, {
        owner: config.user?.id,
        tags: ['expressr', 'document'],
      }).then((matter) => {
        dispatch({ type: 'UPDATE_DOCUMENT', id: doc.id, updates: { mattrId: matter.id } });
      }).catch(() => {
        // Mattr registration failed — non-fatal, document still works
      });

      return doc;
    },
    [config.user?.id]
  );

  const updateDocument = useCallback((id: string, updates: Partial<ExpressrDocument>) => {
    dispatch({ type: 'UPDATE_DOCUMENT', id, updates });
    dispatch({ type: 'SET_UNSAVED', hasUnsavedChanges: true });
  }, []);

  const deleteDocument = useCallback((id: string) => {
    dispatch({ type: 'DELETE_DOCUMENT', id });
  }, []);

  const selectDocument = useCallback((id: string | null) => {
    dispatch({ type: 'SELECT_DOCUMENT', id });
  }, []);

  const updateContent = useCallback(
    (content: string) => {
      if (state.activeDocumentId) {
        const wordCount = content.trim() ? content.trim().split(/\s+/).length : 0;
        dispatch({
          type: 'UPDATE_DOCUMENT',
          id: state.activeDocumentId,
          updates: { content, wordCount },
        });
        dispatch({ type: 'SET_UNSAVED', hasUnsavedChanges: true });
      }
    },
    [state.activeDocumentId]
  );

  const saveDocument = useCallback(async () => {
    if (!activeDocument) return;
    dispatch({ type: 'SET_SAVING', saving: true });
    try {
      if (config.onSave) {
        await config.onSave(activeDocument);
      }
      dispatch({ type: 'SET_UNSAVED', hasUnsavedChanges: false });
    } finally {
      dispatch({ type: 'SET_SAVING', saving: false });
    }
  }, [activeDocument, config]);

  // ── Synthesis (delegates to expressr-lib) ──

  const synthesize = useCallback(
    async (options: SynthesisOptions): Promise<SynthesisResult> => {
      dispatch({ type: 'SET_GENERATING', generating: true });
      try {
        const result = await expressrRef.current.synthesize(options);
        dispatch({ type: 'SET_LAST_SYNTHESIS', result });
        return result;
      } finally {
        dispatch({ type: 'SET_GENERATING', generating: false });
      }
    },
    []
  );

  const insertSynthesis = useCallback(
    (content: string) => {
      if (!activeDocument) return;
      const newContent = activeDocument.content
        ? `${activeDocument.content}\n\n${content}`
        : content;
      updateContent(newContent);
    },
    [activeDocument, updateContent]
  );

  // ── Navigation ──

  const setSidePanel = useCallback((panel: ExpressrState['sidePanel']) => {
    dispatch({ type: 'SET_SIDE_PANEL', panel });
  }, []);

  const setFilter = useCallback((filter: string) => {
    dispatch({ type: 'SET_FILTER', filter });
  }, []);

  // ── Export (delegates to outputtr-lib) ──

  const exportDocument = useCallback(
    async (format: string) => {
      if (!activeDocument) return;
      if (config.onPublish) {
        await config.onPublish(activeDocument, format);
      }
    },
    [activeDocument, config]
  );

  // ── Auto-save ──

  useEffect(() => {
    if (!config.autoSaveInterval || config.autoSaveInterval <= 0) return;
    const timer = setInterval(() => {
      if (state.hasUnsavedChanges && activeDocument) {
        saveDocument();
      }
    }, config.autoSaveInterval);
    return () => clearInterval(timer);
  }, [config.autoSaveInterval, state.hasUnsavedChanges, activeDocument, saveDocument]);

  const setViewMode = useCallback((mode: ViewMode) => {
    dispatch({ type: 'SET_VIEW_MODE', viewMode: mode });
  }, []);

  const setDocumentTheme = useCallback((themeId: string) => {
    dispatch({ type: 'SET_DOCUMENT_THEME', themeId });
  }, []);

  const publishToKeepr = useCallback(
    async (category?: string, tags?: string[]) => {
      if (!activeDocument) return { success: false };
      const result = await keeprRef.current.publish({
        title: activeDocument.title,
        content: activeDocument.content,
        contentType: activeDocument.format ?? 'markdown',
        category: category ?? 'document',
        source: 'expressr',
        sourceId: activeDocument.id,
        tags,
      });
      return { success: true, knowledgeId: result?.knowledgeId };
    },
    [activeDocument]
  );

  const exportViaFylr = useCallback(
    (filename?: string) => {
      const name = filename ?? activeDocument?.title ?? 'untitled';
      return fylrRef.current.export(activeDocument?.content ?? '', name);
    },
    [activeDocument]
  );

  const value: ExpressrContextType = {
    state,
    config,
    expressr: expressrRef.current,
    snapshots: snapshotsRef.current,
    outputtr: outputtrRef.current,
    keepr: keeprRef.current,
    fylr: fylrRef.current,
    mattr: mattrRef.current,
    createDocument,
    updateDocument,
    deleteDocument,
    selectDocument,
    saveDocument,
    updateContent,
    synthesize,
    insertSynthesis,
    setSidePanel,
    setFilter,
    exportDocument,
    setViewMode,
    setDocumentTheme,
    publishToKeepr,
    exportViaFylr,
    activeDocument,
    filteredDocuments,
  };

  return <ExpressrContext.Provider value={value}>{children}</ExpressrContext.Provider>;
}

// ── Hook ──

export function useExpressrContext(): ExpressrContextType {
  const ctx = useContext(ExpressrContext);
  if (!ctx) {
    throw new Error('useExpressrContext must be used within ExpressrProvider');
  }
  return ctx;
}